import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';

// Define available languages
export type Language = 'tr' | 'en' | 'de' | 'ru' | 'ar';
export type RegionCode = 'TR' | 'EN' | 'DE' | 'RU' | 'AR'; 

export interface LanguageContextType {
    language: Language;
    region: RegionCode;
    setLanguage: (language: Language) => void;
    translations: Record<string, string>;
    t: (key: string, params?: Record<string, string | number>) => string;
  }
// Map Language to RegionCode
const languageToRegion: Record<Language, RegionCode> = {
  tr: 'TR',
  en: 'EN',
  de: 'DE',
  ru: 'RU',
  ar: 'AR'
};

// Create initial context value
const defaultLanguageContext: LanguageContextType = {
  language: 'tr',
  region: 'TR',
  setLanguage: () => {},
  translations: {},
  t: (key: string) => key,
};

// Create context
export const LanguageContext = createContext<LanguageContextType>(defaultLanguageContext);

// Custom hook for using the language context
export const useLanguage = () => useContext(LanguageContext);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  // Try to get the language from localStorage or use browser language, default to 'tr'
  const getInitialLanguage = (): Language => {
    const savedLanguage = localStorage.getItem('preferredLanguage') as Language;
    if (savedLanguage && ['tr', 'en', 'de', 'ru', 'ar'].includes(savedLanguage)) {
      return savedLanguage;
    }
    
    // Get browser language
    const browserLang = navigator.language.split('-')[0] as Language;
    if (['tr', 'en', 'de', 'ru', 'ar'].includes(browserLang)) {
      return browserLang;
    }
    
    return 'tr'; // Default language
  };

  const [language, setLanguageState] = useState<Language>(getInitialLanguage());
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const region = languageToRegion[language];

  // Function to set language and save to localStorage
  const setLanguage = (newLanguage: Language) => {
    localStorage.setItem('preferredLanguage', newLanguage);
    setLanguageState(newLanguage);
  };

  // Load language file
  useEffect(() => {
    const loadTranslations = async () => {
      try {
        const translationsModule = await import(`../translations/${language}.json`);
        setTranslations(translationsModule.default);
      } catch (error) {
        console.error(`Failed to load translations for ${language}`, error);
        // Fallback to English if translation file not found
        if (language !== 'en') {
          try {
            const fallbackModule = await import('../translations/en.json');
            setTranslations(fallbackModule.default);
          } catch (fallbackError) {
            console.error('Failed to load fallback translations', fallbackError);
          }
        }
      }
    };

    loadTranslations();
  }, [language]);

  // Translation function
  const t = (key: string): string => {
    return translations[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, region, setLanguage, translations, t }}>
      {children}
    </LanguageContext.Provider>
  );
};